import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import {
  ShieldCheck, Lock, Database, KeyRound, Cpu, EyeOff, Users, Activity,
  FileText, Sparkles, ArrowRight, ArrowUpRight, Check,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { alternateLinkList } from "@/lib/i18n/locale";

export const Route = createFileRoute("/vi/security")({
  head: () => ({
    meta: [
      { title: "Bảo mật & Bảo vệ Dữ liệu — banana.cards" },
      { name: "description", content: "Mã hóa khi lưu trữ và truyền tải, AI ưu tiên quyền riêng tư, không bán dữ liệu. Cách Banana Cards bảo vệ dữ liệu của bạn." },
      { property: "og:title", content: "Bảo mật — banana.cards" },
      { property: "og:description", content: "An toàn của bạn là ưu tiên của chúng tôi. Banana Cards được bảo vệ ngay từ thiết kế." },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/security", "vi"),
  }),
  component: SecurityPage,
});

const PROTECTIONS = [
  { icon: Database, title: "Hạ tầng PostgreSQL", desc: "Nền tảng cơ sở dữ liệu bảo mật, đã được kiểm chứng." },
  { icon: Lock, title: "AES-256 khi lưu trữ", desc: "Dữ liệu được mã hóa khi lưu trữ." },
  { icon: ShieldCheck, title: "TLS khi truyền tải", desc: "Mọi luồng dữ liệu đều mã hóa đầu cuối." },
  { icon: KeyRound, title: "Kiểm soát truy cập", desc: "Row-level security và đặc quyền tối thiểu." },
  { icon: Users, title: "Phân quyền theo vai trò", desc: "Kiểm soát chi tiết cho đội nhóm và quản trị viên." },
  { icon: Cpu, title: "AI ưu tiên quyền riêng tư", desc: "Xử lý bằng Vertex AI cấp doanh nghiệp." },
  { icon: EyeOff, title: "Không dùng để huấn luyện AI", desc: "Dữ liệu khách hàng không bao giờ được dùng để train model." },
  { icon: Activity, title: "Giám sát liên tục", desc: "Kiểm tra chủ động chống lạm dụng và xâm phạm." },
];

const SECTIONS = [
  {
    id: "database",
    icon: Database,
    title: "Bảo mật Cơ sở Dữ liệu",
    body: [
      "Banana Cards sử dụng Supabase để quản lý cơ sở dữ liệu và xử lý dữ liệu backend. Supabase được xây dựng trên PostgreSQL và hỗ trợ các cơ chế bảo mật như Row Level Security, cho phép áp dụng quy tắc truy cập trực tiếp ở cấp cơ sở dữ liệu để người dùng chỉ truy cập được dữ liệu mà họ có quyền.",
      "Supabase mã hóa dữ liệu khách hàng khi lưu trữ bằng AES-256 và khi truyền tải bằng TLS. Khi cần thiết, Banana Cards bổ sung thêm kiểm soát truy cập cấp cơ sở dữ liệu, quy tắc xác thực, phân quyền theo vai trò và quy trình backend bảo mật để bảo vệ dữ liệu người dùng khỏi truy cập trái phép.",
    ],
  },
  {
    id: "ai",
    icon: Cpu,
    title: "Xử lý AI",
    body: [
      "Banana Cards sử dụng Google Vertex AI để cung cấp các tính năng AI chọn lọc, bao gồm làm giàu dữ liệu liên hệ, soạn tin nhắn, hỗ trợ hồ sơ, tóm tắt doanh nghiệp, gợi ý thông minh và hỗ trợ quy trình làm việc.",
      "Điều khoản dịch vụ Vertex AI của Google cam kết rằng Google sẽ không sử dụng dữ liệu khách hàng để huấn luyện hoặc tinh chỉnh các mô hình AI/ML nếu không có sự cho phép trước của khách hàng.",
      "Khi Banana Cards sử dụng Vertex AI cho xử lý AI cấp doanh nghiệp, dữ liệu người dùng được gửi cho các tính năng AI chỉ được xử lý để cung cấp tính năng yêu cầu — và không được Google sử dụng để huấn luyện mô hình nền của mình trừ khi được phép rõ ràng.",
    ],
  },
  {
    id: "no-sale",
    icon: EyeOff,
    title: "Chính sách Không Bán Dữ liệu",
    body: [
      "Banana Cards có Chính sách Không Bán Dữ liệu nghiêm ngặt. Chúng tôi không bán dữ liệu người dùng, dữ liệu liên hệ, hồ sơ khách hàng, hồ sơ doanh nghiệp, tài liệu tải lên, lịch sử trao đổi hoặc thông tin đã xử lý bằng AI cho các nhà quảng cáo, môi giới dữ liệu hay bên thứ ba.",
      "Dữ liệu người dùng chỉ được dùng để cung cấp, bảo vệ, cải thiện và hỗ trợ nền tảng Banana Cards và các dịch vụ liên quan, tuân thủ Điều khoản Dịch vụ, Chính sách Bảo mật và pháp luật hiện hành.",
    ],
  },
  {
    id: "access",
    icon: KeyRound,
    title: "Kiểm soát Truy cập",
    body: [
      "Quyền truy cập vào dữ liệu người dùng chỉ giới hạn cho các hệ thống, nhà cung cấp dịch vụ và nhân sự được ủy quyền khi cần thiết để vận hành, bảo trì, bảo mật hoặc hỗ trợ nền tảng.",
      "Banana Cards có thể áp dụng các cấp độ truy cập khác nhau tùy thuộc vào loại tài khoản người dùng, quyền của đội nhóm, cài đặt quản trị và yêu cầu vận hành.",
      "Người dùng có trách nhiệm bảo vệ tài khoản của mình, bao gồm thông tin đăng nhập, tài khoản email được kết nối, danh bạ tải lên, quyền truy cập của thành viên đội nhóm và liên kết hồ sơ được chia sẻ.",
    ],
  },
  {
    id: "responsibility",
    icon: FileText,
    title: "Xử lý Dữ liệu & Trách nhiệm của Người dùng",
    body: [
      "Người dùng có trách nhiệm đảm bảo rằng mọi thông tin liên hệ, tài liệu, danh thiếp, tệp tin hoặc thông tin bên thứ ba được tải lên Banana Cards đều được thu thập và xử lý hợp pháp.",
      "Người dùng chỉ nên tải lên hoặc xử lý dữ liệu cá nhân khi có sự đồng ý, có quan hệ kinh doanh hợp pháp, cơ sở hợp đồng hoặc cơ sở pháp lý khác theo luật bảo mật hiện hành.",
      "Banana Cards không được sử dụng để lưu trữ, xử lý hoặc chia sẻ dữ liệu thu thập trái phép, dữ liệu cá nhân bị scrape, danh sách spam, cơ sở dữ liệu liên hệ mua bán hoặc thông tin riêng tư trái phép.",
    ],
  },
  {
    id: "limits",
    icon: ShieldCheck,
    title: "Giới hạn Bảo mật",
    body: [
      "Mặc dù Banana Cards áp dụng các biện pháp bảo vệ kỹ thuật, tổ chức và vận hành hợp lý, không có nền tảng trực tuyến nào có thể đảm bảo bảo mật tuyệt đối.",
      "Người dùng nên tránh tải lên thông tin nhạy cảm không cần thiết và giữ quyền truy cập tài khoản chỉ cho các thành viên đáng tin cậy.",
      "Banana Cards có thể đình chỉ, hạn chế hoặc xóa quyền truy cập khi hoạt động có dấu hiệu khả nghi, lạm dụng, bất hợp pháp hoặc vi phạm chính sách của chúng tôi.",
    ],
  },
];

const HIGHLIGHTS = [
  "Hạ tầng cơ sở dữ liệu bảo mật dựa trên PostgreSQL",
  "Mã hóa AES-256 cho dữ liệu khi lưu trữ",
  "Mã hóa TLS cho dữ liệu khi truyền tải",
  "Kiểm soát truy cập tài khoản",
  "Phân quyền đội nhóm theo vai trò",
  "Xử lý AI ưu tiên quyền riêng tư",
  "Dữ liệu khách hàng không được dùng để huấn luyện AI/ML",
  "Không bán dữ liệu người dùng hay dữ liệu liên hệ",
  "Xử lý có trách nhiệm với liên hệ, hồ sơ, tệp tin và thông tin doanh nghiệp",
  "Giám sát liên tục để ngăn chặn lạm dụng và hoạt động khả nghi",
];

function SecurityPage() {
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-foreground text-background">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -top-32 right-1/2 h-[480px] w-[480px] translate-x-1/2 rounded-full bg-brand-lime/30 blur-[120px]"
          aria-hidden
        />

        <div className="relative mx-auto grid max-w-7xl gap-16 px-4 pb-20 pt-24 md:grid-cols-[1.1fr_1fr] md:px-6 md:pt-28">
          <div className="flex flex-col justify-center">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-background/60">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              Bảo mật
            </p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] md:text-6xl lg:text-7xl">
              An toàn của bạn… <em className="italic text-brand-lime">ưu tiên của chúng tôi.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base text-background/75 md:text-lg">
              Tại Banana Cards, dữ liệu của bạn được bảo vệ ngay từ thiết kế. Từ hồ sơ kỹ thuật số và danh bạ đã lưu đến
              tệp tin, thông tin doanh nghiệp và công cụ AI — chúng tôi áp dụng quy trình xử lý dữ liệu bảo mật để giữ
              thông tin của bạn an toàn.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/privacy"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]"
              >
                Chính sách bảo mật
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-background/10 px-6 py-3 text-sm font-bold text-background backdrop-blur transition hover:bg-background/20"
              >
                Báo cáo sự cố
              </Link>
            </div>

            <dl className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-background/15 pt-6">
              <div>
                <dt className="font-display text-3xl leading-none">256</dt>
                <dd className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-background/60">Bit mã hóa AES</dd>
              </div>
              <div>
                <dt className="font-display text-3xl leading-none">0</dt>
                <dd className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-background/60">Dữ liệu bị bán</dd>
              </div>
              <div>
                <dt className="font-display text-3xl leading-none">24/7</dt>
                <dd className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-background/60">Giám sát</dd>
              </div>
            </dl>
          </div>

          <div className="relative hidden md:flex md:items-center md:justify-center">
            <div className="relative h-[420px] w-[420px]">
              <div className="absolute inset-0 rounded-full border border-background/10" />
              <div className="absolute inset-8 rounded-full border border-background/15" />
              <div className="absolute inset-16 rounded-full border border-background/20" />
              <div className="absolute inset-24 rounded-full border border-background/30" />
              <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2"><Chip icon={Lock} label="AES-256" /></div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2"><Chip icon={Database} label="PostgreSQL" /></div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2"><Chip icon={Cpu} label="Vertex AI" /></div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2"><Chip icon={KeyRound} label="RLS" /></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-32 w-32 items-center justify-center rounded-3xl bg-brand-lime text-brand-lime-foreground shadow-[0_0_80px_oklch(0.96_0.21_117/0.4)]">
                  <ShieldCheck className="h-14 w-14" strokeWidth={2.2} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROTECTIONS GRID */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Được xây dựng để bảo vệ</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Tám lớp <em className="italic">an tâm.</em>
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Mã hóa, kiểm soát truy cập, AI ưu tiên quyền riêng tư và lập trường không bán dữ liệu — đan vào mọi phần
              của nền tảng.
            </p>
          </div>

          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {PROTECTIONS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="rounded-2xl bg-muted p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-card">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-base font-bold">{p.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Pillar
              icon={Lock}
              title="Mã hóa mọi nơi"
              desc="AES-256 khi lưu trữ, TLS khi truyền tải. Dữ liệu của bạn được bảo vệ dù đang được lưu hay đang di chuyển giữa các hệ thống."
            />
            <Pillar
              icon={Cpu}
              title="AI ưu tiên quyền riêng tư"
              desc="Chúng tôi sử dụng Vertex AI cấp doanh nghiệp. Dữ liệu khách hàng không bao giờ được dùng để huấn luyện hoặc tinh chỉnh mô hình AI/ML."
              featured
            />
            <Pillar
              icon={EyeOff}
              title="Không bán dữ liệu. Chấm hết."
              desc="Chúng tôi không bán dữ liệu người dùng, liên hệ, hồ sơ, tệp tin hay thông tin đã xử lý bằng AI cho bất kỳ ai. Bao giờ."
            />
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Nhìn nhanh</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                Mọi thứ chúng tôi triển khai <em className="italic">để giữ bạn an toàn.</em>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
                Tóm tắt ngắn gọn, dễ hiểu về các kiểm soát, chính sách và quy trình bảo vệ tài khoản và dữ liệu của bạn
                trên Banana Cards.
              </p>
            </div>
            <ul className="grid gap-3 self-center sm:grid-cols-2">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-start gap-2 rounded-xl bg-muted p-4 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
                  <span className="font-semibold">{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* DETAILED POLICY SECTIONS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-6xl px-4 py-20 md:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Chi tiết</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Các chi tiết, <em className="italic">dễ đọc.</em>
            </h2>
            <p className="mt-4 text-base text-muted-foreground">
              Nền tảng và dịch vụ phần mềm của chúng tôi được vận hành toàn cầu bởi Six Studio Creative FZCO. Tại Việt
              Nam, dịch vụ thẻ vật lý và vận hành địa phương được quản lý bởi Banana Cards Vietnam.
            </p>
          </div>

          <div className="mt-12 grid gap-10 md:grid-cols-[200px_1fr]">
            <aside className="md:sticky md:top-24 md:self-start">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Mục lục</p>
              <nav className="mt-4 grid gap-1.5 border-l border-foreground/10 pl-4">
                {SECTIONS.map((s, i) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="text-sm font-medium text-muted-foreground transition hover:text-foreground"
                  >
                    <span className="mr-2 text-muted-foreground/50">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </aside>

            <div className="space-y-4">
              {SECTIONS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <article key={s.id} id={s.id} className="scroll-mt-24 rounded-3xl bg-card p-6 md:p-10">
                    <div className="flex items-start gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                          Mục {String(i + 1).padStart(2, "0")}
                        </p>
                        <h3 className="mt-1 font-display text-2xl leading-tight md:text-3xl">{s.title}</h3>
                      </div>
                    </div>
                    <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-foreground/85">
                      {s.body.map((p, idx) => (
                        <p key={idx}>{p}</p>
                      ))}
                    </div>
                  </article>
                );
              })}

              <div className="grid gap-3 pt-4 sm:grid-cols-3">
                {[
                  { label: "Chính sách bảo mật", to: "/privacy" },
                  { label: "Điều khoản dịch vụ", to: "/terms" },
                  { label: "Hướng dẫn pháp lý", to: "/legal-guidelines" },
                ].map((r) => (
                  <Link
                    key={r.to}
                    to={r.to}
                    className="group flex items-center justify-between gap-4 rounded-2xl bg-card p-5 transition hover:bg-foreground hover:text-background"
                  >
                    <span className="text-sm font-bold">{r.label}</span>
                    <ArrowUpRight className="h-4 w-4 transition group-hover:rotate-12" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUTRO */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-4 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div
              className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.18),transparent_55%)]"
              aria-hidden
            />
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Kết nối với <em className="italic text-brand-lime">sự tự tin.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Quản lý các mối quan hệ kinh doanh trong khi biết rằng dữ liệu của bạn được xử lý bảo mật, có trách nhiệm
              và lấy quyền riêng tư làm trung tâm.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink
                to="/signup"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground"
              >
                Bắt đầu — miễn phí
              </CtaLink>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold"
              >
                Liên hệ đội bảo mật
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

function Chip({ icon: Icon, label }: { icon: typeof Lock; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-full bg-background px-3 py-1.5 text-foreground shadow-[0_0_24px_oklch(0_0_0/0.4)]">
      <Icon className="h-3.5 w-3.5" />
      <span className="text-[11px] font-bold uppercase tracking-wider">{label}</span>
    </div>
  );
}

function Pillar({
  icon: Icon, title, desc, featured,
}: { icon: typeof Lock; title: string; desc: string; featured?: boolean }) {
  return (
    <div className={`rounded-3xl p-8 ${featured ? "bg-foreground text-background ring-2 ring-foreground" : "bg-card"}`}>
      <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${featured ? "bg-brand-lime text-brand-lime-foreground" : "bg-muted"}`}>
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="mt-6 font-display text-xl md:text-2xl">{title}</h3>
      <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-background/75" : "text-muted-foreground"}`}>{desc}</p>
    </div>
  );
}